// src/pages/markets/board/board/ProfilePlaque.tsx
import { useState } from 'react';
import { Box, InputBase, Tooltip } from '@mui/material';

export interface ProfilePlaqueProps {
    initial: string;
    /** Existing personal names, for the case-insensitive uniqueness check. */
    taken: readonly string[];
    onCommit: (name: string) => void;
    onCancel: () => void;
    /** Phone: a 44 px field with 16 px text. */
    touch?: boolean;
}

export function ProfilePlaque({ initial, taken, onCommit, onCancel, touch }: ProfilePlaqueProps) {
    const [text, setText] = useState(initial);
    const [error, setError] = useState(false);

    const commit = () => {
        const name = text.trim();
        if (!name) return onCancel();
        if (taken.some(t => t.trim().toLowerCase() === name.toLowerCase())) {
            setError(true);
            return;
        }
        onCommit(name);
    };

    return (
        <Tooltip open={error} title="You already have a profile with that name.">
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: touch ? 44 : 26,
                    px: touch ? 1.5 : 1,
                    borderRadius: touch ? '8px' : '4px',
                    flexShrink: 0,
                    border: '1px solid',
                    borderColor: error ? 'error.main' : 'primary.main',
                    minWidth: 120,
                }}
            >
                <InputBase
                    autoFocus
                    value={text}
                    inputProps={{ 'aria-label': 'Profile name', maxLength: 40 }}
                    onChange={e => {
                        setText(e.target.value);
                        setError(false);
                    }}
                    onBlur={commit}
                    onKeyDown={e => {
                        if (e.key === 'Enter') commit();
                        if (e.key === 'Escape') onCancel();
                    }}
                    sx={{ fontSize: touch ? '16px' : '11px', color: 'text.primary' }}
                />
            </Box>
        </Tooltip>
    );
}
